import type { ApiNode, Conditions, Input } from "@/workflow_api/types/workflow";
import type {
  ComparativeValueType,
  LogicalOperator,
  Operation,
  SubConditionData,
  Variable,
} from "../types";
import { Operations } from "../operations";
import { NodeTypes } from "@/workflow_api/types/workflow";
import { parseType } from "@/workflow_api/hooks/variables/useGetVariables";
import type { VariableType } from "../../calculated-field/CalculatedFieldOperation/types";

export const getOperationByTypeAndName = (
  type: string = "string", // TODO: remove default when variables are available
  selectedOperation: string,
): Operation | null => {
  const dataType = Operations[type];
  if (dataType) {
    const selected = dataType.find((op) => op.name === selectedOperation);
    if (selected) {
      return selected;
    }
  }
  return null;
};

/**
 * Deserializes an array input with both string values and variable references.
 * Reconstructs the array in the correct order using the output_name field as order indicator.
 */
const deserializeArrayInput = (
  value: string | number | boolean | string[] | null | undefined,
  references: Input[number]["references"] | undefined,
  variables: Variable[],
): string[] | null => {
  if (!value && (!references || references.length === 0)) {
    return null;
  }

  let arrayValue: string[] = [];

  // Parse value into array if needed
  if (Array.isArray(value)) {
    arrayValue = value as string[];
  } else if (typeof value === "string" && value.startsWith("[")) {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        arrayValue = parsed;
      }
    } catch {
      // If parsing fails and no references, return the string as-is wrapped in array
      if (!references || references.length === 0) {
        return null;
      }
    }
  } else if (
    typeof value === "string" &&
    (!references || references.length === 0)
  ) {
    // Single string value without references, not an array
    return null;
  }

  const itemsWithOrder: Array<{ value: string; order: number }> = [];

  // First, collect variable orders to know which positions are taken
  const takenOrders = new Set<number>();
  if (references && references.length > 0) {
    references.forEach((ref) => {
      const order = ref.output_name ? parseInt(ref.output_name, 10) : undefined;
      if (order !== undefined && !isNaN(order)) {
        takenOrders.add(order);
      }
    });
  }

  // Add string values to positions not taken by variables
  let currentOrder = 0;
  arrayValue.forEach((val) => {
    while (takenOrders.has(currentOrder)) {
      currentOrder++;
    }
    itemsWithOrder.push({ value: val, order: currentOrder });
    currentOrder++;
  });

  // Add variable references with their order
  if (references && references.length > 0) {
    references.forEach((ref) => {
      const fieldId = ref.field_id || ref.field;
      const connection = ref.connection;
      const orderFromOutputName = ref.output_name
        ? parseInt(ref.output_name, 10)
        : undefined;
      const order =
        orderFromOutputName !== undefined && !isNaN(orderFromOutputName)
          ? orderFromOutputName
          : itemsWithOrder.length;

      if (connection) {
        const nodeVariable = variables.find((v) => v.node_id === connection);

        let varToAdd: Variable | null = null;
        if (fieldId) {
          if (
            nodeVariable?.ref_name === NodeTypes.INTEROPERABILITY ||
            nodeVariable?.ref_name === NodeTypes.N8N
          ) {
            varToAdd =
              variables.find(
                (v) => v.node_id === connection && v.output_name === fieldId,
              ) ?? null;
          } else {
            varToAdd = variables.find((v) => v.id === fieldId) ?? null;
          }
        }

        if (varToAdd) {
          itemsWithOrder.push({ value: JSON.stringify(varToAdd), order });
        }
      }
    });
  }

  // Sort by order and extract values
  if (itemsWithOrder.length === 0) {
    return null;
  }

  return itemsWithOrder
    .sort((a, b) => a.order - b.order)
    .map((item) => item.value);
};

/**
 * Get the condition values from the object
 * TODO: update object with the correct type
 * @param config
 * @returns
 */
export const deserializeIfNode = (
  variables: Variable[],
  node: ApiNode,
): {
  selectedVariable: Variable | null;
  selectedOperation: Operation | null;
  comparativeValue: string | Variable | string[] | null;
  logicalOperator: LogicalOperator | null;
  additionalConditions: SubConditionData[];
} | null => {
  if (!node.inputs) return null;
  if (node.inputs.length > 0) {
    const conditionalInput = node.inputs[0];
    // build the conditions
    const conditions: SubConditionData[] = [];
    if (conditionalInput.conditions && conditionalInput.conditions.length > 0) {
      conditionalInput.conditions.forEach((condition) => {
        const operation = condition.type;
        let variable: Variable | null = null;
        let comparative: Variable | string | string[] | null = null;

        // Handle both old format (input/optional_input) and new format (inputs array)
        const firstInput = condition.input || condition.inputs?.[0];
        console.log(firstInput);
        if (firstInput) {
          if (firstInput.global_type && firstInput.value) {
            const nodeId = firstInput.value as string;
            const globalVariable = variables.find(
              (v) =>
                v.node_id === nodeId &&
                v.global_type === firstInput.global_type,
            );
            if (globalVariable) {
              variable = globalVariable;
            } else {
              if (
                firstInput.global_type === "APPLICANT" ||
                firstInput.global_type === "CURRENT_TIME"
              ) {
                const _variable = variables.find(
                  (v) => v.id === firstInput.value,
                );
                variable = _variable || null;
              } else {
                const idSuffix =
                  firstInput.global_type === "NODE_EXECUTION_START"
                    ? "execution_start"
                    : "execution_finish";
                variable = {
                  id: `${nodeId}_${idSuffix}`,
                  name:
                    firstInput.global_type === "NODE_EXECUTION_START"
                      ? "inicio-nodo"
                      : "fin-nodo",
                  type: "date",
                  node_id: nodeId,
                  ref_name: "",
                  global_type: firstInput.global_type,
                };
              }
            }
          } else {
            const sourceConnection = firstInput.source_connections?.[0];

            if (sourceConnection) {
              const nodeVariable = variables.find(
                (v) => v.node_id === sourceConnection?.source_node_id,
              );

              if (
                nodeVariable?.ref_name === NodeTypes.INTEROPERABILITY ||
                nodeVariable?.ref_name === NodeTypes.N8N
              ) {
                variable =
                  variables.find(
                    (v) =>
                      v.node_id === sourceConnection?.source_node_id &&
                      v.output_name === sourceConnection?.output_name,
                  ) ?? null;
              } else if (
                sourceConnection?.field_id &&
                sourceConnection?.output_name
              ) {
                variable =
                  variables.find((v) => {
                    return (
                      v.id ===
                      `${sourceConnection.field_id}-${sourceConnection.output_name}`
                    );
                  }) || null;
                variable =
                  variables.find((v) => {
                    return (
                      v.id ===
                      `${sourceConnection.field_id}-${sourceConnection.output_name}`
                    );
                  }) || null;
                console.log(variables);
              } else {
                variable =
                  variables.find((v) => v.id === sourceConnection?.field_id) ??
                  null;
              }
            } else {
              if (firstInput.name) {
                variable =
                  variables.find((v) => v.id === firstInput.name) ?? null;
              }

              if (!variable) {
                const fieldId = firstInput.field || firstInput.field_id;
                const connection = firstInput.connection;
                if (fieldId) {
                  const nodeVariable = variables.find(
                    (v) => v.node_id === connection,
                  );

                  if (
                    nodeVariable?.ref_name === NodeTypes.INTEROPERABILITY ||
                    nodeVariable?.ref_name === NodeTypes.N8N
                  ) {
                    variable =
                      variables.find(
                        (v) =>
                          v.node_id === connection && v.output_name === fieldId,
                      ) ?? null;
                  } else {
                    variable = variables.find((v) => v.id === fieldId) ?? null;
                  }
                }
              }
            }
          }
        }

        const secondInput = condition.optional_input || condition.inputs?.[1];
        if (secondInput) {
          if (secondInput.global_type && secondInput.value) {
            const nodeId = secondInput.value as string;
            if (
              secondInput.global_type === "APPLICANT" ||
              secondInput.global_type === "CURRENT_TIME"
            ) {
              const _variable = variables.find(
                (v) => v.id === secondInput.value,
              );
              comparative = _variable || null;
            } else {
              const globalVariable = variables.find(
                (v) =>
                  v.id === nodeId && v.global_type === secondInput.global_type,
              );
              if (globalVariable) {
                comparative = globalVariable;
              } else {
                const idSuffix =
                  secondInput.global_type === "NODE_EXECUTION_START"
                    ? "execution_start"
                    : "execution_finish";
                comparative = {
                  id: `${nodeId}_${idSuffix}`,
                  name:
                    secondInput.global_type === "NODE_EXECUTION_START"
                      ? "inicio-nodo"
                      : "fin-nodo",
                  type: "date",
                  node_id: nodeId,
                  ref_name: "",
                  global_type: secondInput.global_type,
                };
              }
            }
          } else {
            const sourceConnection = secondInput.source_connections?.[0];

            if (sourceConnection) {
              const nodeVariable = variables.find(
                (v) => v.node_id === sourceConnection?.source_node_id,
              );
              if (
                nodeVariable?.ref_name === NodeTypes.INTEROPERABILITY ||
                nodeVariable?.ref_name === NodeTypes.N8N
              ) {
                comparative =
                  variables.find(
                    (v) =>
                      v.node_id === sourceConnection?.source_node_id &&
                      v.output_name === sourceConnection?.output_name,
                  ) ?? null;
              } else if (
                sourceConnection?.field_id &&
                sourceConnection?.output_name
              ) {
                comparative =
                  variables.find((v) => {
                    return (
                      v.id ===
                      `${sourceConnection.field_id}-${sourceConnection.output_name}`
                    );
                  }) || null;

                const isVariableType =
                  comparative !== null &&
                  typeof comparative === "object" &&
                  "id" in comparative;
                conditions.push({
                  name: condition.name!,
                  id: condition.id,
                  variable: variable ?? null,
                  operation: getOperationByTypeAndName(
                    variable?.type,
                    operation,
                  ),
                  comparativeValue: comparative ?? "",
                  comparativeValueType: isVariableType ? "variable" : "input",
                });
                return;
              } else {
                comparative =
                  variables.find((v) => v.id === sourceConnection?.field_id) ??
                  null;
              }
            } else {
              const fieldId = secondInput.field || secondInput.field_id;
              const connection = secondInput.connection;

              if (fieldId && connection) {
                const nodeVariable = variables.find(
                  (v) => v.node_id === connection,
                );

                if (
                  nodeVariable?.ref_name === NodeTypes.INTEROPERABILITY ||
                  nodeVariable?.ref_name === NodeTypes.N8N
                ) {
                  comparative =
                    variables.find(
                      (v) =>
                        v.node_id === connection && v.output_name === fieldId,
                    ) ?? null;
                } else if (
                  nodeVariable?.ref_name === NodeTypes.FORM &&
                  nodeVariable?.output_name
                ) {
                  comparative =
                    variables.find(
                      (v) => v.id === `${fieldId}-${nodeVariable.output_name}`,
                    ) ?? null;
                } else {
                  comparative = variables.find((v) => v.id === fieldId) ?? null;
                }
              } else if (
                secondInput.value !== null &&
                secondInput.value !== undefined
              ) {
                const value = secondInput.value;

                if (
                  typeof value === "string" ||
                  typeof value === "number" ||
                  typeof value === "boolean" ||
                  (Array.isArray(value) &&
                    value.every((item) => typeof item === "string"))
                ) {
                  const deserializedArray = deserializeArrayInput(
                    value as string | number | boolean | string[],
                    secondInput.references,
                    variables,
                  );

                  if (deserializedArray) {
                    comparative = deserializedArray;
                  } else if (typeof value === "string") {
                    comparative = value;
                  } else {
                    comparative = String(value);
                  }
                }
              } else if (
                secondInput.references &&
                secondInput.references.length > 0
              ) {
                // Handle case where there's no value but there are references (only variables in array)
                const resultArray: string[] = [];

                secondInput.references.forEach((ref) => {
                  const fieldId = ref.field;
                  const connection = ref.connection;

                  if (fieldId && connection) {
                    const nodeVariable = variables.find(
                      (v) => v.node_id === connection,
                    );

                    let varToAdd: Variable | null = null;
                    if (
                      nodeVariable?.ref_name === NodeTypes.INTEROPERABILITY ||
                      nodeVariable?.ref_name === NodeTypes.N8N
                    ) {
                      varToAdd =
                        variables.find(
                          (v) =>
                            v.node_id === connection &&
                            v.output_name === fieldId,
                        ) ?? null;
                    } else {
                      varToAdd =
                        variables.find((v) => v.id === fieldId) ?? null;
                    }

                    if (varToAdd) {
                      resultArray.push(JSON.stringify(varToAdd));
                    }
                  }
                });

                comparative = resultArray.length > 0 ? resultArray : null;
              } else if (secondInput.name) {
                comparative =
                  variables.find((v) => v.id === secondInput.name) ?? null;
              } else {
                comparative =
                  secondInput.value !== null ? String(secondInput.value) : null;
              }
            }
          }
        }

        const isVariableType =
          comparative !== null &&
          typeof comparative === "object" &&
          "id" in comparative;
        conditions.push({
          name: condition.name!,
          id: condition.id,
          variable: variable ?? null,
          operation: getOperationByTypeAndName(variable?.type, operation),
          comparativeValue: comparative ?? "",
          comparativeValueType: isVariableType ? "variable" : "input",
        });
      });
    }
    const firstCondition = conditions[0];
    const additionalConditions = conditions.slice(1, conditions.length);
    const logical =
      conditionalInput.conditions && conditionalInput.conditions.length > 1
        ? conditionalInput.conditions[1].operator
        : "and";
    return {
      selectedVariable: firstCondition.variable,
      selectedOperation: firstCondition.operation,
      comparativeValue: firstCondition.comparativeValue,
      logicalOperator: logical?.toLowerCase() as LogicalOperator,
      additionalConditions: additionalConditions,
    };
  }

  return null;
};

export const extractConditionFromNodeVariables = (
  variables: Variable[],
  condition: Conditions[0],
): {
  operation: Operation | null;
  comparativeValue: string | Variable | string[] | null;
  comparativeValueType: ComparativeValueType;
  variable: Variable | null;
} => {
  const firstInput = condition.input ?? condition.inputs?.[0];
  if (!firstInput?.source_connections?.[0] || variables.length === 0) {
    return {
      variable: null,
      operation: null,
      comparativeValue: null,
      comparativeValueType: "input",
    };
  }

  const source = firstInput.source_connections[0];
  // TODO: find by the output_name
  const variable = variables.find(
    (v) => v.node_id === source.source_node_id && v.node_id === v.id,
  );

  if (!variable) {
    return {
      variable: null,
      operation: null,
      comparativeValue: null,
      comparativeValueType: "input",
    };
  }

  const parsedField = parseType(variable.type);
  const formattedVariable: Variable = {
    id: variable.id,
    name: variable.name,
    type: parsedField as VariableType,
    node_id: variable.node_id!,
    ref_name: variable.ref_name!,
  };

  const operation = getOperationByTypeAndName(
    parsedField as VariableType,
    condition.type,
  );

  // Extract comparative value
  const secondInput = condition.optional_input ?? condition.inputs?.[1];
  let comparativeValue: string | Variable | string[] | null = null;
  let comparativeValueType: ComparativeValueType = "input";

  if (secondInput) {
    if (
      secondInput.source_connections?.[0]?.field &&
      secondInput.source_connections?.[0]?.output_name
    ) {
      comparativeValueType = "variable";
    } else if (secondInput.source_connections?.[0]?.field) {
      const compField = secondInput.source_connections[0].field;
      const parsedCompField = parseType(compField.type);
      comparativeValue = {
        id: compField.id,
        name: compField.name,
        type: parsedCompField as VariableType,
        node_id: secondInput.source_connections[0].source_node_id,
        ref_name: "",
      };
      comparativeValueType = "variable";
    } else if (secondInput.value !== null && secondInput.value !== undefined) {
      const value = secondInput.value;

      if (
        typeof value === "string" ||
        typeof value === "number" ||
        typeof value === "boolean" ||
        (Array.isArray(value) &&
          value.every((item) => typeof item === "string"))
      ) {
        const deserializedArray = deserializeArrayInput(
          value as string | number | boolean | string[],
          secondInput.references,
          variables,
        );

        if (deserializedArray) {
          comparativeValue = deserializedArray;
          comparativeValueType = "input";
        } else if (typeof value === "string") {
          comparativeValue = value;
          comparativeValueType = "input";
        } else {
          comparativeValue = String(value);
          comparativeValueType = "input";
        }
      }
    }
  }

  return {
    variable: formattedVariable,
    operation,
    comparativeValue,
    comparativeValueType,
  };
};
